import { X, Mail, MessageCircle, Send, Eye, MousePointerClick, AlertTriangle, Users, BarChart3 } from "lucide-react";
import SectionCard from "./SectionCard";
import BarChart from "./charts/BarChart";
import AreaChart from "./charts/AreaChart";
import DonutChart from "./charts/DonutChart";
import { formatNum } from "@/lib/constants";
import { cn } from "@/lib/utils";
import LeyendaEstado from "./pipeline/LeyendaEstado";
import { getContactoMensualByPeriodo, getContactoAgregadoByPeriodo } from "@/mocks/data";

function MetricRow({ label, unique, total, uniqueLabel = "destinatarios", totalLabel = "eventos", color = "#7C3AED" }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-sm text-slate-700">{label}</span>
      </div>
      <div className="text-right">
        <span className="text-sm font-bold text-slate-900 tabular-nums">{formatNum(unique)}</span>
        <span className="text-xs text-slate-500 ml-2">({formatNum(total)} {totalLabel})</span>
      </div>
    </div>
  );
}

export default function ContactoDetailDrawer({ tipo, data, periodo = "Abril 2026", onClose }) {
  if (!data || !tipo) return null;

  const isEmail = tipo === "email";
  const detalle = isEmail ? data.detalle_email : data.detalle_whatsapp;
  if (!detalle) return null;

  // Datos filtrados por período
  const { emailFiltrado, wspFiltrado } = getContactoMensualByPeriodo(periodo);
  const contactoAgg = getContactoAgregadoByPeriodo(periodo);
  const mesesLabel = emailFiltrado.length === 1
    ? emailFiltrado[0].mes
    : `${emailFiltrado[0]?.mes} — ${emailFiltrado[emailFiltrado.length - 1]?.mes}`;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="contacto-drawer">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="sticky top-0 z-10 p-5 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "h-10 w-10 rounded-xl flex items-center justify-center text-white",
                isEmail ? "bg-violet-500" : "bg-emerald-500"
              )}>
                {isEmail ? <Mail className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {isEmail ? "Detalle Email" : "Detalle WhatsApp"}
                </h3>
                <p className="text-xs text-slate-500">Motor de comunicaciones · {periodo}</p>
              </div>
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition"
                    aria-label="Cerrar panel">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          <div className="mt-2">
            <LeyendaEstado estado={detalle.origen} />
          </div>
        </div>

        <div className="p-5 space-y-6">
          {isEmail ? (
            <>
              {/* Funnel de email */}
              <SectionCard title="Funnel de Email" subtitle={`Destinatarios únicos · ${periodo}`}>
                <MetricRow label="Enviados (Send)" unique={detalle.send.destinatarios} total={detalle.send.total} totalLabel="envíos" color="#8B5CF6" />
                <MetricRow label="Entregados (Delivery)" unique={detalle.delivery.destinatarios} total={detalle.delivery.total} totalLabel="entregas" color="#3B82F6" />
                <MetricRow label="Abiertos (Open)" unique={detalle.open.destinatarios} total={detalle.open.total} totalLabel="aperturas" color="#10B981" />
                <MetricRow label="Click" unique={detalle.click.destinatarios} total={detalle.click.total} totalLabel="clicks" color="#F59E0B" />
                <MetricRow label="Rebotados (Bounce)" unique={detalle.bounce.destinatarios} total={detalle.bounce.total} totalLabel="rebotes" color="#EF4444" />
                <MetricRow label="Delay" unique={detalle.delay.destinatarios} total={detalle.delay.total} totalLabel="eventos" color="#F97316" />
                <MetricRow label="Spam (Complaint)" unique={detalle.complaint.destinatarios} total={detalle.complaint.total} totalLabel="quejas" color="#DC2626" />
              </SectionCard>

              {/* Emails por paciente */}
              <SectionCard title="Emails por paciente" subtitle={`Promedio: ${detalle.promedio_por_paciente} emails/mes`}>
                <BarChart
                  categories={detalle.emails_por_paciente.map((e) => e.rango)}
                  series={[{ name: "Pacientes", data: detalle.emails_por_paciente.map((e) => e.pacientes) }]}
                  colors={["#7C3AED"]}
                  height={220}
                  distributed
                />
              </SectionCard>

              {/* Actividad mensual email — filtrada por período */}
              <SectionCard title="Actividad mensual" subtitle={`${mesesLabel} · Datos reales`}>
                <AreaChart
                  categories={emailFiltrado.map((e) => e.mes)}
                  series={[
                    { name: "Enviados", data: emailFiltrado.map((e) => e.enviados) },
                    { name: "Abiertos", data: emailFiltrado.map((e) => e.abiertos) },
                    { name: "Clicks", data: emailFiltrado.map((e) => e.clicks) },
                  ]}
                  colors={["#8B5CF6", "#10B981", "#F59E0B"]}
                  height={240}
                />
              </SectionCard>

              {/* Resumen del período */}
              <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 space-y-2">
                <div className="text-xs font-semibold text-violet-700 uppercase tracking-wider">Resumen {periodo}</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{formatNum(contactoAgg.email.enviados)}</div>
                    <div className="text-xs text-slate-500">enviados</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{contactoAgg.email.open_rate}%</div>
                    <div className="text-xs text-slate-500">open rate</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{contactoAgg.email.click_rate}%</div>
                    <div className="text-xs text-slate-500">click rate</div>
                  </div>
                </div>
              </div>

              {/* Contexto */}
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
                <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Contexto</div>
                <p className="text-sm text-slate-700">
                  <strong>Remitente:</strong> {detalle.remitente}
                </p>
                <p className="text-sm text-slate-700">
                  <strong>Link principal:</strong> {detalle.link_principal}
                </p>
                <p className="text-sm text-slate-700">
                  <strong>Relación con agenda:</strong> Los emails son de confirmación y recordatorio de citas. Los destinatarios representan ~97% de los pacientes con cita en PRM.
                </p>
                <p className="text-sm text-slate-700">
                  <strong>Flujos:</strong> PRE (pre-cita), ANUL (anulación), LAT (latencia), BLdef (bloqueo default), MPRE (multi-pre).
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Funnel de WhatsApp */}
              <SectionCard title="Funnel de WhatsApp" subtitle={`Teléfonos únicos · ${periodo}`}>
                <MetricRow label="Enviados (Sent)" unique={detalle.sent.telefonos} total={detalle.sent.total} totalLabel="mensajes" color="#10B981" />
                <MetricRow label="Entregados (Delivered)" unique={detalle.delivered.telefonos} total={detalle.delivered.total} totalLabel="entregas" color="#06B6D4" />
                <MetricRow label="Leídos (Read)" unique={detalle.read.telefonos} total={detalle.read.total} totalLabel="lecturas" color="#3B82F6" />
                <MetricRow label="Fallidos (Failed)" unique={detalle.failed.telefonos} total={detalle.failed.total} totalLabel="fallos" color="#EF4444" />
              </SectionCard>

              {/* Tipos de mensaje */}
              <SectionCard title="Tipos de mensaje" subtitle="Mensajes vs conversaciones">
                <BarChart
                  categories={detalle.tipos_mensaje.map((t) => t.tipo)}
                  series={[{ name: "Mensajes", data: detalle.tipos_mensaje.map((t) => t.total) }]}
                  colors={["#10B981", "#06B6D4", "#8B5CF6", "#F59E0B", "#3B82F6", "#EC4899"]}
                  height={220}
                  horizontal
                  distributed
                />
              </SectionCard>

              {/* Interacciones bidireccionales */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3">
                <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Interacciones bidireccionales</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{formatNum(detalle.interacciones_bidireccionales)}</div>
                    <div className="text-xs text-slate-500">interacciones (texto + botón)</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums">{formatNum(detalle.telefonos_con_interaccion)}</div>
                    <div className="text-xs text-slate-500">pacientes que respondieron</div>
                  </div>
                </div>
                <p className="text-sm text-slate-700">
                  <strong>Promedio:</strong> {detalle.promedio_interacciones} interacciones por paciente
                </p>
              </div>

              {/* Actividad mensual WhatsApp — filtrada por período */}
              <SectionCard title="Actividad mensual" subtitle={`${mesesLabel} · Datos reales`}>
                <AreaChart
                  categories={wspFiltrado.map((w) => w.mes)}
                  series={[
                    { name: "Enviados", data: wspFiltrado.map((w) => w.enviados) },
                    { name: "Entregados", data: wspFiltrado.map((w) => w.entregados) },
                    { name: "Leídos", data: wspFiltrado.map((w) => w.leidos) },
                  ]}
                  colors={["#10B981", "#06B6D4", "#3B82F6"]}
                  height={240}
                />
              </SectionCard>

              {/* Resumen del período */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-2">
                <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Resumen {periodo}</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{formatNum(contactoAgg.wsp.enviados)}</div>
                    <div className="text-xs text-slate-500">enviados</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{contactoAgg.wsp.read_rate}%</div>
                    <div className="text-xs text-slate-500">read rate</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-slate-900 tabular-nums">{formatNum(contactoAgg.wsp.telefonos_est)}</div>
                    <div className="text-xs text-slate-500">teléfonos</div>
                  </div>
                </div>
              </div>

              {/* Contexto */}
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
                <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Contexto</div>
                <p className="text-sm text-slate-700">
                  <strong>Cuenta:</strong> {detalle.cuenta_business}
                </p>
                <p className="text-sm text-slate-700">
                  <strong>Mensajes vs conversaciones:</strong> Los mensajes enviados incluyen templates iniciales y respuestas automáticas. Las conversaciones reales son textos + botones = interacciones bidireccionales.
                </p>
                <p className="text-sm text-slate-700">
                  <strong>Tasa de lectura real:</strong> {formatNum(detalle.read.telefonos)} de {formatNum(detalle.sent.telefonos)} teléfonos leyeron = {Math.round(detalle.read.telefonos / detalle.sent.telefonos * 100)}%
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
